// ============================================================================
// EFFECT SYSTEM - Manages status effects, buffs, debuffs, and ability effects
// ============================================================================

import { EntityId, Vector2 } from '@/shared/types';
import { generateId } from '@/shared/utils';

// ----------------------------------------------------------------------------
// EFFECT TYPES
// ----------------------------------------------------------------------------

export enum EffectType {
  // Damage over time
  Poison = 'poison',
  Burn = 'burn',
  Bleed = 'bleed',
  
  // Healing over time
  Regen = 'regen',
  HealBurst = 'heal_burst',
  
  // Crowd Control
  Stun = 'stun',
  Slow = 'slow',
  Root = 'root',           // Cannot move but can shoot
  Silence = 'silence',     // Cannot use abilities
  Blind = 'blind',         // Reduced vision
  
  // Defensive
  Shield = 'shield',
  DamageReduction = 'damage_reduction',
  Immunity = 'immunity',
  FrontShield = 'front_shield',    // Directional damage reduction (from front only)
  
  // Offensive
  DamageBoost = 'damage_boost',
  FireRateBoost = 'fire_rate_boost',
  CritBoost = 'crit_boost',
  
  // Movement
  SpeedBoost = 'speed_boost',
  SpeedReduction = 'speed_reduction',
  
  // Stealth
  Cloak = 'cloak',
  Revealed = 'revealed',
  
  // Special
  Marked = 'marked',       // Takes bonus damage
  Invulnerable = 'invulnerable',
  Phased = 'phased',       // Can pass through walls
}

export enum EffectCategory {
  Buff = 'buff',
  Debuff = 'debuff',
  Neutral = 'neutral',
}

// How effects stack when multiple are applied
export enum StackBehavior {
  None = 'none',           // Only one instance, ignore new
  Refresh = 'refresh',     // Reset duration on new application
  Stack = 'stack',         // Add stacks up to max
  Replace = 'replace',     // Replace with new (stronger wins)
  Separate = 'separate',   // Each source has separate instance
}

// ----------------------------------------------------------------------------
// EFFECT DEFINITION
// ----------------------------------------------------------------------------

export interface EffectDefinition {
  type: EffectType;
  category: EffectCategory;
  stackBehavior: StackBehavior;
  maxStacks: number;
  priority: number;        // Higher = processed first (for conflicts)
  cleansable: boolean;     // Can be removed by cleanse abilities
  dispellable: boolean;    // Can be removed by dispel/purge
}

// Effect definitions table
export const EFFECT_DEFINITIONS: Record<EffectType, EffectDefinition> = {
  // DoT effects
  [EffectType.Poison]: {
    type: EffectType.Poison,
    category: EffectCategory.Debuff,
    stackBehavior: StackBehavior.Stack,
    maxStacks: 5,
    priority: 10,
    cleansable: true,
    dispellable: true,
  },
  [EffectType.Burn]: {
    type: EffectType.Burn,
    category: EffectCategory.Debuff,
    stackBehavior: StackBehavior.Refresh,
    maxStacks: 1,
    priority: 10,
    cleansable: true,
    dispellable: true,
  },
  [EffectType.Bleed]: {
    type: EffectType.Bleed,
    category: EffectCategory.Debuff,
    stackBehavior: StackBehavior.Stack,
    maxStacks: 3,
    priority: 10,
    cleansable: false,
    dispellable: true,
  },
  
  // Healing
  [EffectType.Regen]: {
    type: EffectType.Regen,
    category: EffectCategory.Buff,
    stackBehavior: StackBehavior.Replace,
    maxStacks: 1,
    priority: 5,
    cleansable: false,
    dispellable: true,
  },
  [EffectType.HealBurst]: {
    type: EffectType.HealBurst,
    category: EffectCategory.Buff,
    stackBehavior: StackBehavior.None,
    maxStacks: 1,
    priority: 100, // Process heals before damage
    cleansable: false,
    dispellable: false,
  },
  
  // Crowd Control
  [EffectType.Stun]: {
    type: EffectType.Stun,
    category: EffectCategory.Debuff,
    stackBehavior: StackBehavior.Refresh,
    maxStacks: 1,
    priority: 90, // High priority - stops actions
    cleansable: true,
    dispellable: true,
  },
  [EffectType.Slow]: {
    type: EffectType.Slow,
    category: EffectCategory.Debuff,
    stackBehavior: StackBehavior.Replace, // Strongest slow wins
    maxStacks: 1,
    priority: 50,
    cleansable: true,
    dispellable: true,
  },
  [EffectType.Root]: {
    type: EffectType.Root,
    category: EffectCategory.Debuff,
    stackBehavior: StackBehavior.Refresh,
    maxStacks: 1,
    priority: 85,
    cleansable: true,
    dispellable: true,
  },
  [EffectType.Silence]: {
    type: EffectType.Silence,
    category: EffectCategory.Debuff,
    stackBehavior: StackBehavior.Refresh,
    maxStacks: 1,
    priority: 80,
    cleansable: true,
    dispellable: true,
  },
  [EffectType.Blind]: {
    type: EffectType.Blind,
    category: EffectCategory.Debuff,
    stackBehavior: StackBehavior.Refresh,
    maxStacks: 1,
    priority: 60,
    cleansable: true,
    dispellable: true,
  },
  
  // Defensive
  [EffectType.Shield]: {
    type: EffectType.Shield,
    category: EffectCategory.Buff,
    stackBehavior: StackBehavior.Replace, // Stronger shield wins
    maxStacks: 1,
    priority: 95, // Shields absorb before damage calc
    cleansable: false,
    dispellable: true,
  },
  [EffectType.DamageReduction]: {
    type: EffectType.DamageReduction,
    category: EffectCategory.Buff,
    stackBehavior: StackBehavior.Replace,
    maxStacks: 1,
    priority: 94,
    cleansable: false,
    dispellable: true,
  },
  [EffectType.Immunity]: {
    type: EffectType.Immunity,
    category: EffectCategory.Buff,
    stackBehavior: StackBehavior.Refresh,
    maxStacks: 1,
    priority: 99, // Highest - blocks all damage
    cleansable: false,
    dispellable: false,
  },
  [EffectType.FrontShield]: {
    type: EffectType.FrontShield,
    category: EffectCategory.Buff,
    stackBehavior: StackBehavior.Refresh,
    maxStacks: 1,
    priority: 93, // Directional damage reduction
    cleansable: false,
    dispellable: true,
  },
  
  // Offensive
  [EffectType.DamageBoost]: {
    type: EffectType.DamageBoost,
    category: EffectCategory.Buff,
    stackBehavior: StackBehavior.Replace,
    maxStacks: 1,
    priority: 40,
    cleansable: false,
    dispellable: true,
  },
  [EffectType.FireRateBoost]: {
    type: EffectType.FireRateBoost,
    category: EffectCategory.Buff,
    stackBehavior: StackBehavior.Replace,
    maxStacks: 1,
    priority: 40,
    cleansable: false,
    dispellable: true,
  },
  [EffectType.CritBoost]: {
    type: EffectType.CritBoost,
    category: EffectCategory.Buff,
    stackBehavior: StackBehavior.Replace,
    maxStacks: 1,
    priority: 40,
    cleansable: false,
    dispellable: true,
  },
  
  // Movement
  [EffectType.SpeedBoost]: {
    type: EffectType.SpeedBoost,
    category: EffectCategory.Buff,
    stackBehavior: StackBehavior.Replace,
    maxStacks: 1,
    priority: 50,
    cleansable: false,
    dispellable: true,
  },
  [EffectType.SpeedReduction]: {
    type: EffectType.SpeedReduction,
    category: EffectCategory.Debuff,
    stackBehavior: StackBehavior.Replace,
    maxStacks: 1,
    priority: 50,
    cleansable: true,
    dispellable: true,
  },
  
  // Stealth
  [EffectType.Cloak]: {
    type: EffectType.Cloak,
    category: EffectCategory.Buff,
    stackBehavior: StackBehavior.Refresh,
    maxStacks: 1,
    priority: 70,
    cleansable: false,
    dispellable: true,
  },
  [EffectType.Revealed]: {
    type: EffectType.Revealed,
    category: EffectCategory.Debuff,
    stackBehavior: StackBehavior.Refresh,
    maxStacks: 1,
    priority: 75, // Higher than cloak - breaks it
    cleansable: false,
    dispellable: false,
  },
  
  // Special
  [EffectType.Marked]: {
    type: EffectType.Marked,
    category: EffectCategory.Debuff,
    stackBehavior: StackBehavior.Refresh,
    maxStacks: 1,
    priority: 30,
    cleansable: true,
    dispellable: true,
  },
  [EffectType.Invulnerable]: {
    type: EffectType.Invulnerable,
    category: EffectCategory.Buff,
    stackBehavior: StackBehavior.Refresh,
    maxStacks: 1,
    priority: 100,
    cleansable: false,
    dispellable: false,
  },
  [EffectType.Phased]: {
    type: EffectType.Phased,
    category: EffectCategory.Buff,
    stackBehavior: StackBehavior.Refresh,
    maxStacks: 1,
    priority: 60,
    cleansable: false,
    dispellable: false,
  },
};

// ----------------------------------------------------------------------------
// ACTIVE EFFECT INSTANCE
// ----------------------------------------------------------------------------

export interface ActiveEffect {
  id: string;
  type: EffectType;
  sourceId: EntityId;      // Who applied the effect
  targetId: EntityId;      // Who has the effect
  startTime: number;       // Game time when applied
  duration: number;        // Duration in ms (0 = permanent until removed)
  value: number;           // Effect strength (damage/heal per tick, % modifier, etc.)
  stacks: number;          // Current stack count
  tickInterval: number;    // How often to apply (for DoT/HoT), 0 = once
  lastTickTime: number;    // Last time effect ticked
  data: Record<string, unknown> | null; // Extra data for special effects
}

// ----------------------------------------------------------------------------
// ZONE EFFECT (Area effects like poison cloud, healing field)
// ----------------------------------------------------------------------------

export interface ZoneEffect {
  id: string;
  type: EffectType;
  sourceId: EntityId;
  position: Vector2;
  radius: number;
  startTime: number;
  duration: number;
  tickInterval: number;
  lastTickTime: number;
  value: number;
  affectsAllies: boolean;
  affectsEnemies: boolean;
  visualType: ZoneVisualType;
}

export enum ZoneVisualType {
  PoisonCloud = 'poison_cloud',
  FireZone = 'fire_zone',
  HealingField = 'healing_field',
  SlowField = 'slow_field',
  ShieldDome = 'shield_dome',
  StunZone = 'stun_zone',
}

// ----------------------------------------------------------------------------
// EFFECT SYSTEM
// ----------------------------------------------------------------------------

export class EffectSystem {
  private activeEffects: Map<EntityId, ActiveEffect[]> = new Map();
  private zoneEffects: ZoneEffect[] = [];
  
  // Callbacks for when effects are applied/removed
  private onEffectApplied: ((effect: ActiveEffect) => void) | null = null;
  private onEffectRemoved: ((effect: ActiveEffect) => void) | null = null;
  private onEffectTick: ((effect: ActiveEffect, value: number) => void) | null = null;
  private onZoneTick: ((zone: ZoneEffect, targetIds: EntityId[]) => void) | null = null;

  constructor(callbacks?: {
    onEffectApplied?: (effect: ActiveEffect) => void;
    onEffectRemoved?: (effect: ActiveEffect) => void;
    onEffectTick?: (effect: ActiveEffect, value: number) => void;
    onZoneTick?: (zone: ZoneEffect, targetIds: EntityId[]) => void;
  }) {
    if (callbacks) {
      this.onEffectApplied = callbacks.onEffectApplied ?? null;
      this.onEffectRemoved = callbacks.onEffectRemoved ?? null;
      this.onEffectTick = callbacks.onEffectTick ?? null;
      this.onZoneTick = callbacks.onZoneTick ?? null;
    }
  }

  // -------------------------------------------------------------------------
  // EFFECT APPLICATION
  // -------------------------------------------------------------------------

  applyEffect(
    type: EffectType,
    sourceId: EntityId,
    targetId: EntityId,
    duration: number,
    value: number,
    tickInterval: number = 0,
    data?: Record<string, unknown>
  ): ActiveEffect | null {
    const definition = EFFECT_DEFINITIONS[type];
    if (!definition) return null;

    // Get existing effects on target
    let targetEffects = this.activeEffects.get(targetId);
    if (!targetEffects) {
      targetEffects = [];
      this.activeEffects.set(targetId, targetEffects);
    }

    // Check for existing effect of same type
    const existingIndex = targetEffects.findIndex(e => e.type === type);
    const existing = existingIndex >= 0 ? targetEffects[existingIndex] : null;

    // Handle stacking behavior
    switch (definition.stackBehavior) {
      case StackBehavior.None:
        if (existing) return null; // Don't apply
        break;
        
      case StackBehavior.Refresh:
        if (existing) {
          // Reset duration
          existing.startTime = Date.now();
          existing.duration = duration;
          if (value > existing.value) existing.value = value; // Take stronger
          return existing;
        }
        break;
        
      case StackBehavior.Stack:
        if (existing) {
          if (existing.stacks < definition.maxStacks) {
            existing.stacks++;
            existing.startTime = Date.now(); // Refresh duration too
            existing.duration = duration;
          }
          return existing;
        }
        break;
        
      case StackBehavior.Replace:
        if (existing) {
          if (value >= existing.value) {
            // Replace with stronger/equal
            existing.value = value;
            existing.startTime = Date.now();
            existing.duration = duration;
            existing.sourceId = sourceId;
          }
          return existing;
        }
        break;
        
      case StackBehavior.Separate:
        // Allow multiple from different sources
        const existingFromSource = targetEffects.find(
          e => e.type === type && e.sourceId === sourceId
        );
        if (existingFromSource) {
          existingFromSource.startTime = Date.now();
          existingFromSource.duration = duration;
          existingFromSource.value = value;
          return existingFromSource;
        }
        break;
    }

    // Create new effect
    const newEffect: ActiveEffect = {
      id: generateId('effect'),
      type,
      sourceId,
      targetId,
      startTime: Date.now(),
      duration,
      value,
      stacks: 1,
      tickInterval,
      lastTickTime: Date.now(),
      data: data ?? null,
    };

    targetEffects.push(newEffect);
    
    // Handle effect conflicts
    this.resolveConflicts(targetId);
    
    this.onEffectApplied?.(newEffect);
    return newEffect;
  }

  // -------------------------------------------------------------------------
  // CONFLICT RESOLUTION
  // -------------------------------------------------------------------------

  private resolveConflicts(targetId: EntityId): void {
    const effects = this.activeEffects.get(targetId);
    if (!effects) return;

    // Revealed breaks Cloak
    const hasRevealed = effects.some(e => e.type === EffectType.Revealed);
    if (hasRevealed) {
      const cloakIndex = effects.findIndex(e => e.type === EffectType.Cloak);
      if (cloakIndex >= 0) {
        const removed = effects.splice(cloakIndex, 1)[0];
        if (removed) this.onEffectRemoved?.(removed);
      }
    }

    // Immunity/Invulnerable removes debuffs (but they can't be reapplied while active)
    const hasImmunity = effects.some(
      e => e.type === EffectType.Immunity || e.type === EffectType.Invulnerable
    );
    if (hasImmunity) {
      for (let i = effects.length - 1; i >= 0; i--) {
        const effect = effects[i]!;
        const def = EFFECT_DEFINITIONS[effect.type];
        if (def.category === EffectCategory.Debuff) {
          const removed = effects.splice(i, 1)[0];
          if (removed) this.onEffectRemoved?.(removed);
        }
      }
    }
  }

  // -------------------------------------------------------------------------
  // EFFECT REMOVAL
  // -------------------------------------------------------------------------

  removeEffect(targetId: EntityId, effectId: string): boolean {
    const effects = this.activeEffects.get(targetId);
    if (!effects) return false;

    const index = effects.findIndex(e => e.id === effectId);
    if (index < 0) return false;

    const removed = effects.splice(index, 1)[0];
    if (removed) this.onEffectRemoved?.(removed);
    return true;
  }

  removeEffectsByType(targetId: EntityId, type: EffectType): number {
    const effects = this.activeEffects.get(targetId);
    if (!effects) return 0;

    let count = 0;
    for (let i = effects.length - 1; i >= 0; i--) {
      if (effects[i]!.type === type) {
        const removed = effects.splice(i, 1)[0];
        if (removed) {
          this.onEffectRemoved?.(removed);
          count++;
        }
      }
    }
    return count;
  }

  removeAllEffects(targetId: EntityId): void {
    const effects = this.activeEffects.get(targetId);
    if (!effects) return;

    for (const effect of effects) {
      this.onEffectRemoved?.(effect);
    }
    this.activeEffects.delete(targetId);
  }

  cleanse(targetId: EntityId): number {
    const effects = this.activeEffects.get(targetId);
    if (!effects) return 0;

    let count = 0;
    for (let i = effects.length - 1; i >= 0; i--) {
      const effect = effects[i]!;
      const def = EFFECT_DEFINITIONS[effect.type];
      if (def.cleansable) {
        const removed = effects.splice(i, 1)[0];
        if (removed) {
          this.onEffectRemoved?.(removed);
          count++;
        }
      }
    }
    return count;
  }

  dispel(targetId: EntityId): number {
    const effects = this.activeEffects.get(targetId);
    if (!effects) return 0;

    let count = 0;
    for (let i = effects.length - 1; i >= 0; i--) {
      const effect = effects[i]!;
      const def = EFFECT_DEFINITIONS[effect.type];
      if (def.dispellable) {
        const removed = effects.splice(i, 1)[0];
        if (removed) {
          this.onEffectRemoved?.(removed);
          count++;
        }
      }
    }
    return count;
  }

  // -------------------------------------------------------------------------
  // ZONE EFFECTS
  // -------------------------------------------------------------------------

  createZone(
    type: EffectType,
    sourceId: EntityId,
    position: Vector2,
    radius: number,
    duration: number,
    tickInterval: number,
    value: number,
    affectsAllies: boolean,
    affectsEnemies: boolean,
    visualType: ZoneVisualType
  ): ZoneEffect {
    const zone: ZoneEffect = {
      id: generateId('zone'),
      type,
      sourceId,
      position: { ...position },
      radius,
      startTime: Date.now(),
      duration,
      tickInterval,
      lastTickTime: Date.now(),
      value,
      affectsAllies,
      affectsEnemies,
      visualType,
    };
    this.zoneEffects.push(zone);
    return zone;
  }

  removeZone(zoneId: string): boolean {
    const index = this.zoneEffects.findIndex(z => z.id === zoneId);
    if (index < 0) return false;
    this.zoneEffects.splice(index, 1);
    return true;
  }

  getZones(): ZoneEffect[] {
    return [...this.zoneEffects];
  }

  // -------------------------------------------------------------------------
  // QUERIES
  // -------------------------------------------------------------------------

  hasEffect(targetId: EntityId, type: EffectType): boolean {
    const effects = this.activeEffects.get(targetId);
    return effects?.some(e => e.type === type) ?? false;
  }

  getEffect(targetId: EntityId, type: EffectType): ActiveEffect | undefined {
    const effects = this.activeEffects.get(targetId);
    return effects?.find(e => e.type === type);
  }

  getEffects(targetId: EntityId): ActiveEffect[] {
    return this.activeEffects.get(targetId) ?? [];
  }

  getEffectsByCategory(targetId: EntityId, category: EffectCategory): ActiveEffect[] {
    const effects = this.activeEffects.get(targetId);
    if (!effects) return [];
    return effects.filter(e => EFFECT_DEFINITIONS[e.type].category === category);
  }

  // Computed modifiers from effects
  getSpeedModifier(targetId: EntityId): number {
    const effects = this.activeEffects.get(targetId);
    if (!effects) return 1;

    let modifier = 1;
    for (const effect of effects) {
      switch (effect.type) {
        case EffectType.SpeedBoost:
          modifier *= 1 + effect.value / 100;
          break;
        case EffectType.SpeedReduction:
        case EffectType.Slow:
          modifier *= 1 - effect.value / 100;
          break;
        case EffectType.Root:
        case EffectType.Stun:
          modifier = 0;
          break;
      }
    }
    return Math.max(0, modifier);
  }

  getDamageModifier(targetId: EntityId): number {
    const effects = this.activeEffects.get(targetId);
    if (!effects) return 1;

    let modifier = 1;
    for (const effect of effects) {
      if (effect.type === EffectType.DamageBoost) {
        modifier *= 1 + effect.value / 100;
      }
    }
    return modifier;
  }

  getDamageReduction(targetId: EntityId): number {
    const effects = this.activeEffects.get(targetId);
    if (!effects) return 0;

    let reduction = 0;
    for (const effect of effects) {
      if (effect.type === EffectType.DamageReduction) {
        reduction = Math.max(reduction, effect.value);
      }
      if (effect.type === EffectType.Immunity || effect.type === EffectType.Invulnerable) {
        return 100; // Full immunity
      }
    }
    return reduction;
  }

  getFireRateModifier(targetId: EntityId): number {
    const effects = this.activeEffects.get(targetId);
    if (!effects) return 1;

    let modifier = 1;
    for (const effect of effects) {
      if (effect.type === EffectType.FireRateBoost) {
        modifier *= 1 + effect.value / 100;
      }
      if (effect.type === EffectType.Stun) {
        modifier = 0;
      }
    }
    return modifier;
  }

  isStunned(targetId: EntityId): boolean {
    return this.hasEffect(targetId, EffectType.Stun);
  }

  isRooted(targetId: EntityId): boolean {
    return this.hasEffect(targetId, EffectType.Root);
  }

  isSilenced(targetId: EntityId): boolean {
    return this.hasEffect(targetId, EffectType.Silence);
  }

  isCloaked(targetId: EntityId): boolean {
    return this.hasEffect(targetId, EffectType.Cloak) && 
           !this.hasEffect(targetId, EffectType.Revealed);
  }

  isPhased(targetId: EntityId): boolean {
    return this.hasEffect(targetId, EffectType.Phased);
  }

  isInvulnerable(targetId: EntityId): boolean {
    return this.hasEffect(targetId, EffectType.Invulnerable) ||
           this.hasEffect(targetId, EffectType.Immunity);
  }

  // -------------------------------------------------------------------------
  // UPDATE
  // -------------------------------------------------------------------------

  update(_gameTime: number, getEntitiesInRadius: (pos: Vector2, radius: number) => EntityId[]): void {
    const now = Date.now();

    // Update active effects
    for (const [targetId, effects] of this.activeEffects) {
      for (let i = effects.length - 1; i >= 0; i--) {
        const effect = effects[i]!;
        
        // Check expiration
        if (effect.duration > 0 && now >= effect.startTime + effect.duration) {
          const removed = effects.splice(i, 1)[0];
          if (removed) this.onEffectRemoved?.(removed);
          continue;
        }

        // Process ticks (DoT, HoT)
        if (effect.tickInterval > 0 && now >= effect.lastTickTime + effect.tickInterval) {
          effect.lastTickTime = now;
          const tickValue = effect.value * effect.stacks;
          this.onEffectTick?.(effect, tickValue);
        }
      }

      // Clean up empty arrays
      if (effects.length === 0) {
        this.activeEffects.delete(targetId);
      }
    }

    // Update zone effects
    for (let i = this.zoneEffects.length - 1; i >= 0; i--) {
      const zone = this.zoneEffects[i]!;
      
      // Check expiration
      if (now >= zone.startTime + zone.duration) {
        this.zoneEffects.splice(i, 1);
        continue;
      }

      // Process zone ticks
      if (now >= zone.lastTickTime + zone.tickInterval) {
        zone.lastTickTime = now;
        const entitiesInZone = getEntitiesInRadius(zone.position, zone.radius);
        if (entitiesInZone.length > 0) {
          this.onZoneTick?.(zone, entitiesInZone);
        }
      }
    }
  }

  // -------------------------------------------------------------------------
  // SERIALIZATION (for network sync)
  // -------------------------------------------------------------------------

  getState(): { effects: Map<EntityId, ActiveEffect[]>; zones: ZoneEffect[] } {
    return {
      effects: new Map(this.activeEffects),
      zones: [...this.zoneEffects],
    };
  }

  setState(state: { effects: Map<EntityId, ActiveEffect[]>; zones: ZoneEffect[] }): void {
    this.activeEffects = new Map(state.effects);
    this.zoneEffects = [...state.zones];
  }

  clear(): void {
    this.activeEffects.clear();
    this.zoneEffects = [];
  }
}

// ----------------------------------------------------------------------------
// HELPER CONSTANTS
// ----------------------------------------------------------------------------

export const TILE_SIZE = 32;

// Convert tiles to pixels for ability ranges
export function tilesToPixels(tiles: number): number {
  return tiles * TILE_SIZE;
}

// Convert pixels to tiles
export function pixelsToTiles(pixels: number): number {
  return pixels / TILE_SIZE;
}

// Common ability ranges in pixels
export const ABILITY_RANGES = {
  MELEE: tilesToPixels(1),        // 32px
  SHORT: tilesToPixels(3),         // 96px
  MEDIUM: tilesToPixels(5),        // 160px
  LONG: tilesToPixels(8),          // 256px
  VERY_LONG: tilesToPixels(12),    // 384px
  
  // Specific ability ranges
  BLINK: tilesToPixels(3),
  REVEAL: tilesToPixels(5),
  HEAL_BURST: tilesToPixels(4),
  SHOCKWAVE: tilesToPixels(4),
  GRENADE: tilesToPixels(6),
  TURRET_RANGE: tilesToPixels(5),
  POISON_CLOUD: tilesToPixels(3),
  DOME_SHIELD: tilesToPixels(4),
};
